import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import * as api from "../services/api";
import {
  signOutGoogle,
  isGoogleOAuthConfigured,
} from "../services/googleOAuth";
import { useToast } from "./ToastContext";

const AuthContext = createContext(null);

function clearAuthQueryParams() {
  const newUrl = window.location.pathname + window.location.hash;
  window.history.replaceState({}, "", newUrl);
}

function mapAuthErrorMessage(errorCode) {
  switch (errorCode) {
    case "no_code":
      return "Google did not return an authorisation code.";
    case "not_configured":
      return "Google Sign-In is not configured on the server.";
    case "invalid_state":
      return "The sign-in request expired before Khronos could finish it. Please try again.";
    case "no_token":
      return "Google completed sign-in but did not return an ID token.";
    case "invalid_token":
      return "Google returned a token that Khronos could not verify.";
    default:
      return "Authentication failed.";
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Set when start-up could not reach the server. The token is still stored and
  // still good, so the session is deferred rather than ended — we pick it up
  // again the moment connectivity returns.
  const [sessionDeferred, setSessionDeferred] = useState(false);
  const toast = useToast();

  const setCurrentUser = useCallback((nextUser) => {
    setUser(nextUser);
  }, []);

  const refreshCurrentUser = useCallback(async () => {
    const userData = await api.getCurrentUser();
    setUser(userData);
    setError(null);
    return userData;
  }, []);

  // Check for existing token or token in URL (from OAuth redirect)
  useEffect(() => {
    let isActive = true;

    const initAuth = async () => {
      const updateAuthError = (message, toastTitle = "Sign-in failed") => {
        if (!isActive) return;
        setError(message);
        toast.showToast({
          title: toastTitle,
          description: message,
          duration: 7000,
        });
      };

      const bootstrapCurrentUser = async (source) => {
        try {
          const userData = await api.getCurrentUser();

          if (!isActive) {
            return false;
          }

          setUser(userData);
          setError(null);

          if (source === "oauth-redirect") {
            toast.showToast({
              title: "Signed in",
              description: `Connected as ${userData.email}.`,
              duration: 4000,
            });
          }

          return "ok";
        } catch (err) {
          console.error(`Failed to fetch user after ${source}:`, err);

          if (!isActive) {
            return "aborted";
          }

          // Offline is not a rejection. Precaching the app shell (#75) makes a
          // no-signal cold start a routine path, and clearing the token here
          // would silently sign people out every time they opened the app in a
          // basement gym — forcing a fresh Google round-trip once they resurface.
          // The stored token is untouched and we retry when the network returns.
          if (api.isNetworkError(err)) {
            setSessionDeferred(true);
            return "unreachable";
          }

          api.logout();
          setUser(null);

          if (source === "oauth-redirect") {
            updateAuthError(
              "Google sign-in completed, but Khronos could not finish signing you in. Please try again.",
              "Sign-in incomplete",
            );
          }

          return "rejected";
        }
      };

      // First check URL for token (from OAuth redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get("token");
      const authStatus = urlParams.get("auth");
      let authenticatedFromRedirect = false;

      if (tokenFromUrl && authStatus === "success") {
        const tokenResult = api.setToken(tokenFromUrl);

        if (!tokenResult.ok) {
          updateAuthError(
            "Google sign-in completed, but this browser could not securely store your sign-in. Please disable strict storage blocking or try a regular browser tab.",
            "Storage unavailable",
          );
          clearAuthQueryParams();
        } else {
          authenticatedFromRedirect =
            (await bootstrapCurrentUser("oauth-redirect")) === "ok";
          clearAuthQueryParams();
        }
      } else if (authStatus === "failed") {
        updateAuthError(mapAuthErrorMessage(urlParams.get("error")));
        clearAuthQueryParams();
      }

      const token = authenticatedFromRedirect ? null : api.getToken();

      if (token) {
        const storedTokenResult = await bootstrapCurrentUser("stored-token");

        // Only an actual rejection means the saved sign-in is spent. Being
        // unreachable leaves it intact, so saying otherwise would be a lie.
        if (storedTokenResult === "rejected" && isActive) {
          setError(
            "Your saved sign-in is no longer valid. Please sign in again.",
          );
        }
      }

      if (isActive) {
        setLoading(false);
      }
    };

    initAuth();

    // Listen for logout events from API
    const handleLogout = () => {
      setUser(null);
      api.logout();
    };

    window.addEventListener("auth:logout", handleLogout);
    return () => {
      isActive = false;
      window.removeEventListener("auth:logout", handleLogout);
    };
  }, [toast]);

  // Resume a session that start-up had to defer for want of a network. Without
  // this the user stays signed out — with a perfectly good token — until they
  // happen to reload.
  useEffect(() => {
    if (!sessionDeferred) return;

    const resume = () => {
      refreshCurrentUser()
        .then(() => setSessionDeferred(false))
        .catch(() => {});
    };

    window.addEventListener("online", resume);
    return () => window.removeEventListener("online", resume);
  }, [sessionDeferred, refreshCurrentUser]);

  // Sign in with Google ID token (for popup flow)
  const login = useCallback(
    async (idToken) => {
      if (!isGoogleOAuthConfigured()) {
        const message = "Google Sign-In is not configured.";
        setError(message);
        toast.showToast({
          title: "Sign-in unavailable",
          description: message,
          duration: 7000,
        });
        throw new Error("Google Sign-In is not configured");
      }

      setError(null);
      setLoading(true);

      try {
        // Send the Google ID token to our API
        const response = await api.loginWithGoogle(idToken);

        setUser(response.user);
        toast.showToast({
          title: "Signed in",
          description: `Connected as ${response.user.email}.`,
          duration: 4000,
        });
        return response.user;
      } catch (err) {
        console.error("Login error:", err);
        const message = err.message || "Login failed";
        setError(message);
        toast.showToast({
          title: "Sign-in failed",
          description: message,
          duration: 7000,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  // Sign out
  const logout = useCallback(async () => {
    try {
      // Sign out from Google (clears cookies)
      signOutGoogle();
    } catch (err) {
      console.error("Google sign out error:", err);
    }

    api.logout();
    setUser(null);
    setError(null);
    toast.showToast({
      title: "Signed out",
      description: "You have been signed out on this device.",
      duration: 3000,
    });
  }, [toast]);

  const value = {
    user,
    loading,
    error,
    // Signed out only because the server was unreachable, not because the
    // sign-in was refused — the difference between "sign in again" and "wait".
    sessionDeferred,
    isAuthenticated: !!user,
    login,
    logout,
    setCurrentUser,
    refreshCurrentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
